import { Form, type UseFormReturn, useForm } from '@faceup/form'
import { defaultVariant, type FormVariant, twMerge } from '@faceup/ui'
import { type InferType, type Schema, yup } from '@faceup/validation'
import type { ReactNode } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'
import { type InputMode, useAFFormItem } from './hooks/useAFFormItem'
import { getFieldId, useScrollToFirstError } from './hooks/useScrollToFirstError'

type FormSubmitButtonProps =
  | { submitButtonText: 'send'; customSubmitButtonText?: never }
  | { submitButtonText?: never; customSubmitButtonText: ReactNode }

const fragments = {
  AFForm_item: graphql(`
    fragment AFForm_item on AF_FormItemInterface {
      id
      order
      isRequired
      type
      labelTranslation(language: $language) {
        translation
      }
      hintTranslation(language: $language) {
        translation
      }
      ... on AF_FormItemMultilineText {
        maxLength
      }
      ... on AF_FormItemWithOptionsInterface {
        options {
          id
          translation(language: $language) {
            id
            translation
            language
          }
        }
      }
      ... on AF_FormItemWithOptionsWithOtherInterface {
        hasOtherOption
      }
      ... on AF_FormItemMultiSelect {
        maxResponses
        minResponses
      }
      ... on AF_FormItemMatrix {
        matrixOptions: options {
          id
          order
        }
        questions {
          id
          order
        }
      }
      ... on AF_FormItemScale {
        startLabelTranslation(language: $language) {
          translation
        }
        endLabelTranslation(language: $language) {
          translation
        }
      }
    }
  `),
}

type FormItemSchema = ReturnType<ReturnType<typeof useAFFormItem>['getFormInputValueSchema']>
type FormSchema = Record<string, FormItemSchema>
type FormData = Record<string, InferType<FormItemSchema>>

type CustomField<T = unknown> = {
  id: string
  schema: Schema
  render: (form: UseFormReturn) => ReactNode
  defaultValue?: T
}

type AFFormProps = {
  items: FragmentType<typeof fragments.AFForm_item>[]
  onSubmit: (values: FormData) => Promise<boolean>
  defaultValues?: Partial<Record<string, InferType<FormItemSchema>>>
  excludedItemIds?: string[]
  headerFields?: CustomField[]
  footerFields?: CustomField[]
  isSubmitButtonDisabled?: boolean
  submitButtonLabel?: ReactNode
  submitErrorMessage?: ReactNode
  submitErrorMessageWrapperClassName?: string
  hideSubmitButtons?: boolean
  validationContext?: Record<string, unknown>
  inputMode?: InputMode
  renderVoiceRecording?: (form: UseFormReturn, itemId: string) => ReactNode
  buttonsPosition?: 'under' | 'under-center' | 'under-block'
  submitButtonWrapperClassName?: string
  variant?: FormVariant
}

export const AFForm = ({
  items: _items,
  onSubmit,
  defaultValues,
  excludedItemIds = [],
  headerFields = [],
  footerFields = [],
  isSubmitButtonDisabled = false,
  submitButtonLabel = undefined,
  submitErrorMessage,
  submitErrorMessageWrapperClassName,
  hideSubmitButtons = false,
  validationContext,
  inputMode = 'text',
  renderVoiceRecording,
  buttonsPosition = 'under-center',
  submitButtonWrapperClassName,
  variant = defaultVariant,
}: AFFormProps) => {
  const items = _items.map(item => getFragmentData(fragments.AFForm_item, item))
  const sortedItems = [...items].sort((a, b) => a.order - b.order)
  const { getFormInputValueSchema, renderFormInput } = useAFFormItem()

  const itemSchema = sortedItems.reduce<FormSchema>((acc, formItem) => {
    return {
      ...acc,
      [formItem.id]: getFormInputValueSchema(formItem, { inputMode }),
    }
  }, {})

  const customFieldsSchema = headerFields
    .concat(footerFields)
    .reduce<Record<string, Schema>>((acc, customField) => {
      return {
        ...acc,
        [customField.id]: customField.schema,
      }
    }, {})

  const schema = yup.object().shape({
    ...itemSchema,
    ...customFieldsSchema,
  })

  const itemDefaultValues = sortedItems.reduce<
    Record<string, { type: string; value: string | undefined }>
  >(
    (acc, formItem) => ({
      ...acc,
      [formItem.id]: {
        type: formItem.type,
        value: undefined,
      },
    }),
    {}
  )

  const customFieldsDefaultValues = [...headerFields, ...footerFields].reduce<
    Record<string, unknown>
  >((acc, customField) => {
    return {
      ...acc,
      [customField.id]: customField.defaultValue,
    }
  }, {})

  const form = useForm({
    schema,
    afterSubmit: 'persistValues',
    defaultValues: {
      ...(itemDefaultValues as InferType<typeof schema>),
      ...customFieldsDefaultValues,
      ...(defaultValues as InferType<typeof schema>),
    },
    context: validationContext,
  })

  const itemsToRender = sortedItems.filter(item => !excludedItemIds.includes(item.id))

  const { submitCount, errors } = form.formState

  useScrollToFirstError({
    submitCount,
    errors,
    orderedFieldIds: [
      ...headerFields.map(f => f.id),
      ...itemsToRender.map(item => item.id),
      ...footerFields.map(f => f.id),
    ],
  })

  const submitButtonProps: FormSubmitButtonProps =
    submitButtonLabel === undefined
      ? {
          submitButtonText: 'send',
        }
      : { customSubmitButtonText: submitButtonLabel }

  return (
    <Form
      variant={variant}
      buttonsPosition={buttonsPosition}
      submitButtonWrapperClassName={submitButtonWrapperClassName}
      hideSubmitButtons={hideSubmitButtons}
      {...submitButtonProps}
      form={form}
      isSuccessMessageDisplayed={false}
      isUnsavedAlertDisabled={true}
      isSubmitButtonDisabled={isSubmitButtonDisabled}
      onSubmit={onSubmit}
    >
      <div className='flex flex-col gap-6'>
        {headerFields.map(customField => (
          <div key={customField.id} id={getFieldId(customField.id)}>
            {customField.render(form)}
          </div>
        ))}
        {itemsToRender.map(item => (
          <div key={item.id} id={getFieldId(item.id)}>
            {renderFormInput(form, item, { inputMode, renderVoiceRecording })}
          </div>
        ))}
        {footerFields.map(customField => (
          <div key={customField.id} id={getFieldId(customField.id)}>
            {customField.render(form)}
          </div>
        ))}
        {submitErrorMessage && submitCount > 0 && Object.keys(errors).length > 0 && (
          <output
            className={twMerge(
              'block text-colorError text-center',
              submitErrorMessageWrapperClassName
            )}
          >
            {submitErrorMessage}
          </output>
        )}
      </div>
    </Form>
  )
}
